/* eslint-disable react/no-array-index-key */
import {Batch, BatchQuantityError} from '@cypro/engine/schema'
import {withFieldGroup} from '@cypro/ui/integrations/form'
import {Box, Text, Menu, IconButton, Switch} from '@chakra-ui/react'
import {getShortDateFormatterUTC} from '@cypro/ui/utils'
import {LuArrowLeftRight, LuEllipsisVertical, LuLink2, LuTrash2} from 'react-icons/lu'
import {useTranslate} from '@tolgee/react'
import {HiCheck, HiX} from 'react-icons/hi'
import {AddBatchDialog, AddBatchDialogProps} from '../../dialogs/add-batch-dialog'
import {dialog} from '@/modules/layout'

function onOpenChangeDialog(props: AddBatchDialogProps) {
  dialog.open('add-batch-dialog', {
    children: <AddBatchDialog {...props} />,
    contentProps: {minWidth: '50vw'},
  })
}
import {OutOfStockAlert} from '../out-of-stock-alert'

interface QuantityBatchFormProps {
  batches: Batch[]
  onRemove?: () => void
  onIsolateBatch?: () => void
  addBatch: (batchId: string) => void
  prescribedNameIds?: string[]
  isConsumable?: boolean
  drugCatalogItemIds?: string[]
  batchErrors?: BatchQuantityError[]
  onValuesChange?: () => void
}

export const QuantityBatchForm = withFieldGroup({
  defaultValues: {
    batchId: '',
    quantity: 0,
    sharedWith: [],
  } as {batchId: string; quantity: number; sharedWith: string[]},
  props: {} as QuantityBatchFormProps,
  render: ({
    group,
    batches,
    prescribedNameIds,
    drugCatalogItemIds,
    onRemove,
    onIsolateBatch,
    addBatch,
    batchErrors,
    onValuesChange,
  }) => {
    const {t} = useTranslate('common')
    const batch = batches.find(b => b.id === group.getFieldValue('batchId'))
    const batchError = batchErrors?.find(e => e.batchId === batch?.id)
    const sharedWith = group.getFieldValue('sharedWith') || []

    function onChangeBatch(batchId: string) {
      group.setFieldValue('batchId', batchId)
      addBatch(batchId)
    }

    const batchName = batch?.tradeName?.name || batch?.consumable?.name || 'Batch name'
    return (
      <Box display="flex" flexDirection="column" gap="2">
        <Box display="flex" flexDirection="row" gap="2" alignItems="center" paddingInline="2" paddingBlock="1.5">
          <Box maxWidth="75px" display="flex" flexDirection="row" alignItems="center">
            <group.AppField
              name="quantity"
              listeners={{
                onBlur: () => {
                  onValuesChange?.()
                },
              }}
            >
              {field => <field.NumberInput min={0} label={t('quantity')} hideLabel bgColor="white" placeholder="0" />}
            </group.AppField>
          </Box>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Box display="flex" flexDirection="row" gap="1" alignItems="center">
              <Text fontSize="sm" fontWeight="medium">
                {batchName}
              </Text>
              {sharedWith.length > 0 && (
                <Box width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                  <LuLink2 color="blue" />
                </Box>
              )}
            </Box>
            <Text fontSize="12px" fontWeight="normal" color="fg.muted">
              {t('batch_decription', {
                quantity: batch?.quantity,
                lotNumber: batch?.lotNumber,
                expiryDate: batch?.expiryDate ? getShortDateFormatterUTC().format(batch.expiryDate) : '-',
              })}
            </Text>
          </Box>
          <Box>
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton size="sm" variant="ghost" title={t('more_actions')}>
                  <LuEllipsisVertical />
                </IconButton>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content minWidth="250px">
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="2"
                    justifyContent="space-between"
                    paddingInline="2"
                    paddingBlock="1.5"
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap="2">
                      <LuLink2 />
                      {t('isolate_item')}
                    </Box>
                    <Switch.Root
                      disabled={sharedWith.length === 0}
                      checked={sharedWith.length === 0}
                      onCheckedChange={e => {
                        if (e.checked && onIsolateBatch) {
                          onIsolateBatch()
                        }
                      }}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control>
                        <Switch.Thumb>
                          <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                            <HiCheck />
                          </Switch.ThumbIndicator>
                        </Switch.Thumb>
                      </Switch.Control>
                    </Switch.Root>
                  </Box>
                  <Menu.Item
                    value="change"
                    onSelect={() =>
                      onOpenChangeDialog({
                        drugCatalogItemIds: drugCatalogItemIds,
                        prescribedNameIds: prescribedNameIds,
                        onSelectBatch: onChangeBatch,
                      })
                    }
                  >
                    <LuArrowLeftRight />
                    {t('change')}
                  </Menu.Item>
                  <Menu.Separator />
                  <Menu.Item
                    value="delete"
                    color="fg.error"
                    _hover={{bg: 'bg.error', color: 'fg.error'}}
                    onSelect={onRemove}
                  >
                    <LuTrash2 /> {t('delete')}
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </Box>
        </Box>
        {batchError && <OutOfStockAlert outOfStockItems={[batchName]} />}
      </Box>
    )
  },
})
